import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Info } from 'lucide-react';
import { addDays, differenceInCalendarDays, format, parseISO, subDays } from 'date-fns';
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { fmtNumber } from '@/lib/financeFormat';

// Token usage summed server-side from each decision's llm_calls — the same
// per-call numbers the decision drawer shows (GET /ai-dispatcher/usage).

// The backend reports each call's agent family as haiku | sonnet | other.
// Model is shown as the tier, agent as the family name — one table so the
// filter, the chart legend and the By model table cannot drift apart.
const AGENT_LABELS = {
  haiku: { tier: 'Tier 1', agent: 'Haiku' },
  sonnet: { tier: 'Tier 2', agent: 'Sonnet' },
  other: { tier: 'Other', agent: 'Other' },
};
const labelsFor = (family) => AGENT_LABELS[family] || AGENT_LABELS.other;

const AGENT_OPTIONS = [
  { value: 'all', label: 'Haiku and Sonnet' },
  { value: 'haiku', label: AGENT_LABELS.haiku.agent },
  { value: 'sonnet', label: AGENT_LABELS.sonnet.agent },
];

// Validated categorical slots from index.css (--viz-series-*), each with its
// own light and dark step. Assigned in fixed order — slot 1, then 2, then 3 —
// in both views, so the first series is always blue and the second orange.
const COLORS = {
  haiku: 'var(--viz-series-1)',
  sonnet: 'var(--viz-series-2)',
  other: 'var(--viz-series-3)',
  tokens_in: 'var(--viz-series-1)',
  tokens_out: 'var(--viz-series-2)',
  // Projection is context, not data: neutral ink, dashed, with a faint band
  // (Evergreen, "Four Ways to Show Projections": dash it, fade or gray it,
  // show the range) — so it can never be mistaken for a fourth series.
  forecast: 'var(--boss-text-secondary)',
  // Recessive chart chrome: one step off the surface, hairline, solid.
  grid: 'var(--boss-border)',
};

// With both agents selected, the bars split by agent — that is the question
// "both" is asking. With one agent selected, they split by direction instead.
const SERIES_BY_AGENT = [
  { key: 'haiku_tokens', label: AGENT_LABELS.haiku.tier, color: COLORS.haiku },
  { key: 'sonnet_tokens', label: AGENT_LABELS.sonnet.tier, color: COLORS.sonnet },
];
const SERIES_BY_DIRECTION = [
  { key: 'tokens_in', label: 'Input', color: COLORS.tokens_in },
  { key: 'tokens_out', label: 'Output', color: COLORS.tokens_out },
];

const VIEWER_TZ = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
})();

const isoDay = (d) => format(d, 'yyyy-MM-dd');

const tokens = (n) => fmtNumber(n, { compact: true });

/**
 * Least-squares line through `values`, with x = array index. Valid as a
 * per-day slope only because `daily` is zero-filled — one row per calendar
 * day, so index i is exactly day i of the range. A two-point slope
 * (last − first) would hinge on whichever two days happen to be the ends.
 * Returns null when there is nothing to fit (under two days, or all zero).
 */
export function linearTrend(values) {
  const n = values.length;
  if (n < 2) return null;
  const xMean = (n - 1) / 2;
  const yMean = values.reduce((s, y) => s + y, 0) / n;
  if (yMean === 0) return null;
  let num = 0;
  let den = 0;
  values.forEach((y, x) => {
    num += (x - xMean) * (y - yMean);
    den += (x - xMean) ** 2;
  });
  const slope = num / den;
  const intercept = yMean - slope * xMean;
  return { slope, intercept };
}

// Below this many days with usage, a fitted line is noise that looks like
// signal (two busy days then quiet ones fit a steep "decline"). The opt-in
// trend line is still drawn, but labelled "low data"; the forecast's minimum
// (minActiveDaysFor) is capped at this.
const MIN_TREND_DAYS = 7;

// Projection filter. The horizon is also the trailing window the run rate
// averages over — the same N both ways, so the KPI and the chart line both
// read "last N → next N". 0 turns the projection off.
const PROJECTION_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 7, label: 'Next 7 days' },
  { value: 14, label: 'Next 14 days' },
  { value: 30, label: 'Next 30 days' },
];
const DEFAULT_PROJECTION_DAYS = 14;

// Half the window, capped at the trend line's bar: a fixed 7 would demand
// usage on every single day of a 7-day window.
const minActiveDaysFor = (days) => Math.min(MIN_TREND_DAYS, Math.ceil(days / 2));

// Two-sided 80% normal quantile — FPP3's default narrower band (Table 5.1).
const Z_80 = 1.2816;

/**
 * Run-rate forecast: the trailing window's average day, projected forward.
 * Quiet days stay in the average — a zero day is real usage too, and
 * dropping them would overstate the rate. Deliberately not the regression
 * slope extended forward: with weeks of history at most, a slope projected
 * 14 days out amplifies noise, while an average is stable and is what
 * "on pace for" means on a billing page.
 *
 * This is the "mean method" of Hyndman & Athanasopoulos, Forecasting:
 * Principles and Practice (3rd ed., §5.2), and the band is its 80%
 * prediction interval (§5.5): a point forecast alone hides how uncertain it
 * is, and with sparse, spiky usage that uncertainty is the main message.
 *   per day:  ȳ ± z·σ̂·√(1 + 1/T)       (spread of one day + error in ȳ)
 *   total:    h·ȳ ± z·σ̂·√(h + h²/T)    (h days of spread + h× the error in ȳ)
 * Bands are floored at 0 — usage can't go negative. Daily usage is
 * zero-heavy rather than normal, so the band is a guide, not a guarantee.
 *
 * `values` is per-day totals ending today; `days` is both the trailing
 * window and the horizon. Returns null below `minActiveDaysFor(days)` days
 * with usage in the window — too little to project.
 */
export function runRateForecast(values, days) {
  const window = values.slice(-days);
  if (window.filter((v) => v > 0).length < minActiveDaysFor(days)) return null;
  const T = window.length;
  const perDay = window.reduce((s, v) => s + v, 0) / T;
  // Sample standard deviation (n − 1); T ≥ 4 is guaranteed by the guard above.
  const sd = Math.sqrt(window.reduce((s, v) => s + (v - perDay) ** 2, 0) / (T - 1));
  const dayHalfWidth = Z_80 * sd * Math.sqrt(1 + 1 / T);
  const total = perDay * days;
  const totalHalfWidth = Z_80 * sd * Math.sqrt(days + (days * days) / T);
  return {
    perDay,
    perDayLow: Math.max(0, perDay - dayHalfWidth),
    perDayHigh: perDay + dayHalfWidth,
    total,
    totalLow: Math.max(0, total - totalHalfWidth),
    totalHigh: total + totalHalfWidth,
    basisDays: T,
    days,
  };
}

const LEGEND_ORDER = ['haiku_tokens', 'tokens_in', 'sonnet_tokens', 'tokens_out', 'other_tokens', 'trend', 'forecast'];
const legendRank = (dataKey) => {
  const i = LEGEND_ORDER.indexOf(dataKey);
  return i === -1 ? LEGEND_ORDER.length : i;
};

const signed = (n, fmt) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmt(Math.abs(n))}`;

/**
 * The same-length range ending the day before `from` — the "previous period"
 * a customer compares against. Both bounds are inclusive, like the API's.
 */
export function previousRange(from, to) {
  const start = parseISO(from);
  const days = differenceInCalendarDays(parseISO(to), start) + 1;
  return { from: isoDay(subDays(start, days)), to: isoDay(subDays(start, 1)), days };
}

// Compact stat tile: the chart is the primary content here, so the tiles stay
// secondary in size and weight (Carbon: the most important data gets the
// largest area). Value in the UI sans with proportional figures — monospace
// digits look loose on a standalone number.
function Kpi({ label, value, hint, title }) {
  return (
    <div className="glass-card px-3 py-2.5 min-w-0" title={title}>
      <div className="text-[11px] text-boss-text-secondary truncate">{label}</div>
      <div className="text-lg font-semibold leading-tight text-boss-text mt-0.5">{value}</div>
      {hint && <div className="text-[11px] text-boss-text-muted mt-0.5 truncate">{hint}</div>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  // On today's row the forecast only anchors the line's start; listing it
  // there would read as a second measurement of a day that has a real one.
  // The band ([low, high]) gets its own "likely range" line below.
  const shown = payload.filter(
    (p) =>
      p.value != null &&
      p.dataKey !== 'forecastRange' &&
      (p.dataKey !== 'forecast' || row.isForecast),
  );
  return (
    <div className="rounded-md border border-boss-border bg-boss-surface px-3 py-2 shadow-sm text-[12px]">
      <div className="text-[11px] text-boss-text-muted mb-1">{label}</div>
      {shown.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-3">
          {/* Key mirrors the mark: a swatch for bars, a dashed stroke for the forecast line. */}
          {p.dataKey === 'forecast' ? (
            <span className="w-3 border-t-2 border-dashed" style={{ borderColor: p.color }} />
          ) : (
            <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
          )}
          <span className="text-boss-text-secondary">{p.name}</span>
          <span className="font-semibold text-boss-text ml-auto">{fmtNumber(p.value)}</span>
        </div>
      ))}
      {row.isForecast && row.forecastRange && (
        <div className="text-boss-text-muted mt-0.5">
          Likely {tokens(row.forecastRange[0])}–{tokens(row.forecastRange[1])} (80%)
        </div>
      )}
      <div className="border-t border-boss-border mt-1.5 pt-1.5 text-boss-text-muted">
        {row.isForecast
          ? `Projected · average of the last ${row.forecastBasis} days`
          : `${fmtNumber(row.calls)} calls · ${fmtNumber(row.emails)} emails`}
      </div>
    </div>
  );
}

export default function UsageTab() {
  const [agent, setAgent] = useState('all');
  const [fromDate, setFromDate] = useState(() => isoDay(subDays(new Date(), 29)));
  const [toDate, setToDate] = useState(() => isoDay(new Date()));
  // Client-side only: the projection is computed from `daily`, so changing
  // it re-renders without a refetch (it is not a fetchUsage dependency).
  const [projectionDays, setProjectionDays] = useState(DEFAULT_PROJECTION_DAYS);
  // Off by default: the trend (fit over the whole range) and the forecast
  // (average of the trailing window) are different models and rarely meet at
  // "Today", so showing both is opt-in. Display-only, like the projection.
  const [showTrend, setShowTrend] = useState(false);
  const [data, setData] = useState(null);
  // Totals for the same-length range just before this one; null when either
  // date is cleared or that request fails — it only feeds one KPI.
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    const getUsage = async (from, to) => {
      const params = new URLSearchParams({ agent, tz: VIEWER_TZ });
      if (from) params.set('from_date', from);
      if (to) params.set('to_date', to);
      const res = await api.get(`${API_URL}/api/ai-dispatcher/usage?${params.toString()}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body?.detail || 'Failed to load token usage');
      return body;
    };
    try {
      const prev = fromDate && toDate ? previousRange(fromDate, toDate) : null;
      const [body, prevBody] = await Promise.all([
        getUsage(fromDate, toDate),
        prev ? getUsage(prev.from, prev.to).catch(() => null) : null,
      ]);
      setData(body);
      setPrevious(prevBody ? { totals: prevBody.totals, days: prev.days } : null);
    } catch (err) {
      setError(err?.message || 'Failed to load token usage');
    } finally {
      setLoading(false);
    }
  }, [agent, fromDate, toDate]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Fit from the first day with any usage: zero days before it mean "not live
  // yet", not "quiet", and would flatten the line toward zero. Zero days
  // after it are real quiet days and stay in the fit.
  const trend = useMemo(() => {
    const daily = data?.daily || [];
    const start = daily.findIndex((d) => d.total_tokens > 0);
    if (start === -1) return null;
    // Still null when there's nothing to fit (usage only on the last day).
    const fit = linearTrend(daily.slice(start).map((d) => d.total_tokens));
    const activeDays = daily.filter((d) => d.total_tokens > 0).length;
    return fit && { ...fit, start, lowData: activeDays < MIN_TREND_DAYS };
  }, [data]);

  // Only a range ending today can be projected forward; a past window's
  // "next 14 days" already happened.
  const todayIso = isoDay(new Date());
  const endsToday = toDate === todayIso;
  const forecast = useMemo(
    () =>
      endsToday && projectionDays > 0
        ? runRateForecast((data?.daily || []).map((d) => d.total_tokens), projectionDays)
        : null,
    [data, endsToday, projectionDays],
  );

  const chartData = useMemo(() => {
    const rows = (data?.daily || []).map((d, i) => ({
      ...d,
      label: format(parseISO(d.day), 'MMM d'),
      // Floored at 0: a steep decline can fit below zero at the range's
      // end, which would drag the y-axis negative for a quantity that can't be.
      // undefined before the fit starts, so Recharts draws no line there.
      trend:
        trend && i >= trend.start
          ? Math.max(0, Math.round(trend.slope * (i - trend.start) + trend.intercept))
          : undefined,
    }));
    if (!forecast || rows.length === 0) return rows;

    // Future days are rows with no bar values, so the x-axis extends and
    // only the forecast is drawn there. Today carries the line value and a
    // zero-width band, so the line starts at the last real bar and the band
    // fans out from today — today is known, uncertainty starts tomorrow.
    const perDay = Math.round(forecast.perDay);
    const band = [Math.round(forecast.perDayLow), Math.round(forecast.perDayHigh)];
    const lastDay = parseISO(rows[rows.length - 1].day);
    rows[rows.length - 1] = {
      ...rows[rows.length - 1],
      forecast: perDay,
      forecastRange: [perDay, perDay],
    };
    for (let k = 1; k <= forecast.days; k += 1) {
      const day = addDays(lastDay, k);
      rows.push({
        day: isoDay(day),
        label: format(day, 'MMM d'),
        forecast: perDay,
        forecastRange: band,
        isForecast: true,
        forecastBasis: forecast.basisDays,
      });
    }
    return rows;
  }, [data, trend, forecast]);

  // The last observed day's axis label — where the "Today" divider sits.
  const todayLabel = data?.daily?.length
    ? format(parseISO(data.daily[data.daily.length - 1].day), 'MMM d')
    : null;

  // A model id matching neither family would otherwise vanish from the chart
  // while still counting in the totals above it.
  const series = useMemo(() => {
    if (agent !== 'all') return SERIES_BY_DIRECTION;
    const hasOther = (data?.daily || []).some((d) => d.other_tokens > 0);
    return hasOther
      ? [...SERIES_BY_AGENT, { key: 'other_tokens', label: 'Other', color: COLORS.other }]
      : SERIES_BY_AGENT;
  }, [agent, data]);
  const totals = data?.totals;
  const perEmail = totals?.emails ? Math.round(totals.total_tokens / totals.emails) : null;

  // Period-over-period: the change a customer expects "+24%" to mean.
  const change = useMemo(() => {
    if (!totals || !previous) return { value: '—' };
    const cur = totals.total_tokens;
    const prev = previous.totals.total_tokens;
    const period = `prior ${previous.days} days`;
    if (prev === 0) return cur > 0 ? { value: 'New', hint: `No usage in ${period}` } : { value: '—' };
    const pct = Math.round(((cur - prev) / prev) * 100);
    return { value: signed(pct, (n) => `${n}%`), hint: `vs ${tokens(prev)} in ${period}` };
  }, [totals, previous]);

  return (
    <div className="space-y-4" data-testid="ai-dispatcher-usage">
      <div className="glass-card overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-boss-border">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label htmlFor="usage-filter-agent" className="block text-xs text-boss-text-secondary mb-1">
                Agent
              </label>
              <select
                id="usage-filter-agent"
                className="boss-input"
                value={agent}
                onChange={(e) => setAgent(e.target.value)}
              >
                {AGENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="usage-filter-from" className="block text-xs text-boss-text-secondary mb-1">
                From
              </label>
              <input
                id="usage-filter-from"
                type="date"
                className="boss-input px-1"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="usage-filter-to" className="block text-xs text-boss-text-secondary mb-1">
                To
              </label>
              <input
                id="usage-filter-to"
                type="date"
                className="boss-input px-1"
                value={toDate}
                min={fromDate || undefined}
                // No future dates: the API zero-fills every day in the range,
                // so tomorrow would chart as "no usage" and dilute the
                // previous-period comparison. `max` only bounds the picker in
                // some browsers, so a typed date is clamped too (yyyy-mm-dd
                // strings compare in date order).
                max={todayIso}
                onChange={(e) => setToDate(e.target.value > todayIso ? todayIso : e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="usage-filter-projection" className="block text-xs text-boss-text-secondary mb-1">
                Projection
              </label>
              <select
                id="usage-filter-projection"
                className="boss-input"
                value={projectionDays}
                // <select> values are strings; the run-rate math needs a number.
                onChange={(e) => setProjectionDays(Number(e.target.value))}
              >
                {PROJECTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {/* Always available; with sparse data the line is drawn but its
                legend entry says "low data" so the reader knows it's rough. */}
            <label className="flex items-center gap-2 text-xs pb-2 text-boss-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={showTrend}
                onChange={(e) => setShowTrend(e.target.checked)}
                className="rounded border-boss-border"
              />
              Trend line
            </label>
            <div className="text-[11px] text-boss-text-muted pb-2">Days in {VIEWER_TZ}</div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="boss-alert-danger m-4 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-boss-text">{error}</p>
              <button onClick={fetchUsage} className="text-xs text-violet-400 hover:text-violet-300 mt-1">
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Spacing is explicit margins on each block, not space-y: in dev the
            visual-edits plugin wraps `{cond && …}` children in display:contents
            spans, which swallow space-y's margins. The KPI→chart gap (32px) is
            deliberately larger than the gap between tiles (12px) so the two
            read as separate groups. */}
        {!loading && !error && totals && (
          <div className="p-4">
            <div
              className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${
                projectionDays > 0 ? 'xl:grid-cols-7' : 'xl:grid-cols-6'
              }`}
            >
              <Kpi label="Total tokens" value={tokens(totals.total_tokens)} />
              <Kpi label="Input tokens" value={tokens(totals.tokens_in)} />
              <Kpi label="Output tokens" value={tokens(totals.tokens_out)} />
              <Kpi label="LLM calls" value={fmtNumber(totals.calls)} hint={`${fmtNumber(totals.emails)} emails`} />
              <Kpi
                label="Tokens per email"
                value={perEmail == null ? '—' : tokens(perEmail)}
              />
              <Kpi label="Change vs previous period" value={change.value} hint={change.hint} />
              {projectionDays > 0 && (
                <Kpi
                  label={`Projected next ${projectionDays} days`}
                  value={forecast ? tokens(forecast.total) : '—'}
                  hint={
                    forecast
                      ? `Likely ${tokens(forecast.totalLow)}–${tokens(forecast.totalHigh)}`
                      : endsToday
                        ? `Needs ${minActiveDaysFor(projectionDays)} active days`
                        : 'Set To to today'
                  }
                  title={
                    forecast
                      ? `Average of the last ${forecast.basisDays} days (≈ ${tokens(forecast.perDay)}/day), ` +
                        `projected ${forecast.days} days ahead. Likely range is an 80% prediction interval.`
                      : endsToday
                        ? `A projection needs usage on at least ${minActiveDaysFor(projectionDays)} of the last ${projectionDays} days.`
                        : 'Projections start from today — set the To date to today.'
                  }
                />
              )}
            </div>

            {totals.calls_without_tokens > 0 && (
              <div className="mt-3 flex items-start gap-2 text-xs text-boss-text-secondary">
                <Info size={14} className="shrink-0 mt-0.5" />
                <span>
                  {fmtNumber(totals.calls_without_tokens)} of {fmtNumber(totals.calls)} calls reported no
                  token counts (the owner-pick call) and are counted as 0. Totals run slightly low.
                </span>
              </div>
            )}

            {totals.calls === 0 ? (
              <div className="mt-8 py-10 text-center text-sm text-boss-text-muted">
                No LLM calls in this range.
              </div>
            ) : (
              <div className="mt-8" style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 16, right: 8, left: 0, bottom: 4 }}>
                    {/* Solid hairline grid: dashes are reserved for the forecast. */}
                    <CartesianGrid stroke={COLORS.grid} vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: 'var(--boss-text-secondary)' }}
                      axisLine={{ stroke: 'var(--boss-border)' }}
                      tickLine={false}
                      minTickGap={12}
                    />
                    <YAxis
                      tickFormatter={tokens}
                      tick={{ fontSize: 10, fill: 'var(--boss-text-secondary)' }}
                      axisLine={{ stroke: 'var(--boss-border)' }}
                      tickLine={false}
                      width={48}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--boss-accent-muted)' }} />
                    {/* Legend text in neutral ink: identity comes from the
                        swatch beside it, never from coloured text. */}
                    <Legend
                      wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                      // Recharts sorts alphabetically by default; data series
                      // first (in slot order), then the reference lines.
                      itemSorter={(item) => legendRank(item.dataKey)}
                      formatter={(value) => (
                        <span style={{ color: 'var(--boss-text-secondary)' }}>{value}</span>
                      )}
                    />
                    {/* Band first so it sits behind everything: the 80%
                        prediction interval, a faint wash of the forecast ink. */}
                    {forecast && (
                      <Area
                        dataKey="forecastRange"
                        name="Likely range"
                        type="linear"
                        stroke="none"
                        fill={COLORS.forecast}
                        fillOpacity={0.08}
                        legendType="none"
                        activeDot={false}
                        isAnimationActive={false}
                      />
                    )}
                    {/* Thin columns (≤24px) with a 2px surface-coloured gap
                        between stacked segments: wide saturated blocks read
                        loud, and the gap keeps neighbours distinct. */}
                    {series.map((s, i) => (
                      <Bar
                        key={s.key}
                        dataKey={s.key}
                        name={s.label}
                        stackId="tokens"
                        fill={s.color}
                        stroke="var(--boss-bg-surface)"
                        strokeWidth={1}
                        maxBarSize={24}
                        radius={i === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                        isAnimationActive={false}
                      />
                    ))}
                    {/* Fitted to observed days, so solid — dashes mean "projected".
                        Opt-in via the "Trend line" toggle (see showTrend). */}
                    {trend && showTrend && (
                      <Line
                        dataKey="trend"
                        name={trend.lowData ? 'Trend (low data)' : 'Trend'}
                        type="linear"
                        stroke="var(--boss-text-muted)"
                        strokeWidth={1.5}
                        legendType="plainline"
                        dot={false}
                        activeDot={false}
                        isAnimationActive={false}
                      />
                    )}
                    {forecast && (
                      <Line
                        dataKey="forecast"
                        name="Forecast"
                        type="linear"
                        stroke={COLORS.forecast}
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        legendType="plainline"
                        dot={false}
                        activeDot={false}
                        isAnimationActive={false}
                      />
                    )}
                    {/* Where observed data ends and the projection begins. */}
                    {forecast && todayLabel && (
                      <ReferenceLine
                        x={todayLabel}
                        stroke="var(--boss-border-active)"
                        label={{
                          value: 'Today',
                          position: 'top',
                          fontSize: 10,
                          fill: 'var(--boss-text-muted)',
                        }}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>

      {!loading && !error && data?.by_model?.length > 0 && (
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-boss-border text-sm font-medium text-boss-text">
            By model
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-boss-text-secondary text-left">
                  <th className="px-4 py-2 font-medium">Model</th>
                  <th className="px-4 py-2 font-medium">Agent</th>
                  <th className="px-4 py-2 font-medium text-right">Calls</th>
                  <th className="px-4 py-2 font-medium text-right">Input</th>
                  <th className="px-4 py-2 font-medium text-right">Output</th>
                  <th className="px-4 py-2 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.by_model.map((m) => (
                  <tr key={m.model} className="border-t border-boss-border">
                    {/* Two versions of one family share a tier, so the raw
                        id stays reachable on hover to tell the rows apart. */}
                    <td className="px-4 py-2 text-boss-text" title={m.model}>
                      {labelsFor(m.agent).tier}
                    </td>
                    <td className="px-4 py-2 text-boss-text-secondary">{labelsFor(m.agent).agent}</td>
                    <td className="px-4 py-2 text-right font-mono text-boss-text">
                      {fmtNumber(m.calls)}
                      {m.calls_without_tokens > 0 && (
                        <span
                          className="text-boss-text-muted"
                          title="Calls that reported no token counts"
                        >
                          {' '}({fmtNumber(m.calls_without_tokens)} no count)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-boss-text">{fmtNumber(m.tokens_in)}</td>
                    <td className="px-4 py-2 text-right font-mono text-boss-text">{fmtNumber(m.tokens_out)}</td>
                    <td className="px-4 py-2 text-right font-mono text-boss-text">{fmtNumber(m.total_tokens)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
